// client/src/services/poa.ts
// API client for the POA system.

import type { POAFormState, POACreateResponse, POAPrefillResponse, POADetail, SignRepRequest } from '../types/poa';

export async function createPOA(form: POAFormState): Promise<POACreateResponse> {
  const res = await fetch('/api/poa-create', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(form),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }

  return res.json();
}

export async function getPOADetail(id: string): Promise<POADetail> {
  const res = await fetch(`/api/poa/${id}`, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

export async function signRep(req: SignRepRequest): Promise<{ ok: boolean; allSigned: boolean }> {
  const res = await fetch('/api/poa-sign-rep', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

export async function voidPOA(poaId: string): Promise<void> {
  const res = await fetch('/api/poa-void', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ poaId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
}

export async function submitPOA(poaId: string): Promise<void> {
  const res = await fetch('/api/poa-submit', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ poaId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
}

export async function sendToClient(poaId: string): Promise<{ ok: boolean; correlationId: string }> {
  const res = await fetch('/api/poa-send-client', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ poaId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

export interface RemindClientResult {
  ok:             boolean;
  lastReminderAt: string;
  reminderCount:  number;
  hadSigningUrl:  boolean;
}

/**
 * Nudge a taxpayer whose Form 2848 is still unsigned. Rate limited server-side to one
 * per 24 hours — a 429 comes back with a human-readable message when it's too soon.
 */
export async function remindClient(poaId: string): Promise<RemindClientResult> {
  const res = await fetch('/api/poa-remind-client', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ poaId }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}

export async function notifyRep(payload: {
  poaId: string; repOrder: number;
  to: string; subject: string; body: string; bcc?: string;
}): Promise<void> {
  const res = await fetch('/api/poa-notify-rep', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Server error ${res.status}`);
  }
}

export async function getPOAPrefill(clientCode: string): Promise<POAPrefillResponse> {
  const res = await fetch(`/api/poa-prefill?clientCode=${encodeURIComponent(clientCode)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Server error ${res.status}`);
  }
  return res.json();
}
